import React, { useState } from 'react';
import { Waves, FolderKanban, GitFork, Clock, Link2 } from 'lucide-react';
import OrgMemberAvatar from '../../components/ui/OrgMemberAvatar';
import { Tooltip } from '../../components/ui/Tooltip';
import { memberNameClass, memberRoleClass } from '../../components/members/memberViewTheme';
import { contributorRowAvatarUrl } from '../../utils/githubAvatarUrl';

function LinkedContributorChipAvatar({ contributor }: any) {
  const [failed, setFailed] = useState(false);
  const src = contributorRowAvatarUrl(contributor);
  const login = (contributor?.githubUsername || '').trim();
  if (!src || failed) {
    return (
      <span
        className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border-2 border-dark-border bg-dark-elevated text-[10px] font-semibold text-slate-400"
        aria-hidden
      >
        @
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={login ? `@${login}` : ''}
      width={28}
      height={28}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      className="h-7 w-7 flex-shrink-0 rounded-full object-cover border-2 border-dark-border bg-dark-elevated"
      onError={() => setFailed(true)}
    />
  );
}

/**
 * TanStack Table column definitions for the members list.
 */
export function buildMembersTableColumns({
  canManageMembers,
  onLinkGitHubMember,
}: any) {
  return [
    {
      id: 'displayName',
      accessorFn: (row) => (row.username || row.email || '').toLowerCase(),
      header: 'Member',
      cell: ({ row }) => {
        const m = row.original;
        return (
          <div className="flex items-center gap-2.5">
            <OrgMemberAvatar member={m} size="sm" />
            <div className="min-w-0">
              <p className={`${memberNameClass} truncate`}>{m.username || m.email}</p>
              {m.username && m.email && <p className={`${memberRoleClass} truncate`}>{m.email}</p>}
              {m.role?.name ? (
                <span className="mt-1 inline-block rounded bg-dark-elevated px-1.5 py-0.5 text-xs text-slate-400">
                  {m.role.name}
                </span>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'lastLoginAt',
      sortUndefined: 'last',
      header: 'Last login',
      cell: ({ row }) => {
        const raw = row.original.lastLoginAt;
        if (!raw) {
          return <span className="text-sm text-slate-500">Never</span>;
        }
        const d = new Date(raw);
        const label = Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
        return (
          <span className="inline-flex items-center gap-1 text-sm text-slate-400" title={label}>
            <Clock className="w-4 h-4 text-slate-500 flex-shrink-0" aria-hidden />
            {label}
          </span>
        );
      },
    },
    {
      id: 'github',
      accessorFn: (row) =>
        Array.isArray(row.linkedContributors)
          ? row.linkedContributors.map((c) => c.githubUsername).join(' ')
          : '',
      header: 'GitHub',
      enableSorting: false,
      cell: ({ row }) => {
        const m = row.original;
        const links = m.linkedContributors;
        const hasLinks = Array.isArray(links) && links.length > 0;

        if (!canManageMembers) {
          if (!hasLinks) {
            return <span className="text-sm text-slate-500">—</span>;
          }
          return (
            <div className="flex flex-shrink-0 items-center -space-x-1.5">
              {links.slice(0, 4).map((c, i) => (
                <Tooltip key={`${c.githubId}-${c.githubUsername}-${i}`} content={`@${c.githubUsername}`}>
                  <LinkedContributorChipAvatar contributor={c} />
                </Tooltip>
              ))}
            </div>
          );
        }

        if (!hasLinks) {
          return (
            <button
              type="button"
              className="btn-secondary-mdl text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onLinkGitHubMember(m);
              }}
            >
              Link
            </button>
          );
        }

        return (
          <div className="flex items-center gap-2 min-w-0 group">
            <div className="flex flex-shrink-0 items-center -space-x-1.5">
              {links.slice(0, 4).map((c, i) => (
                <Tooltip key={`${c.githubId}-${c.githubUsername}-${i}`} content={`@${c.githubUsername}`}>
                  <LinkedContributorChipAvatar contributor={c} />
                </Tooltip>
              ))}
            </div>
            <button
              type="button"
              className="btn-icon-mdl btn-icon-mdl-sm shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                onLinkGitHubMember(m);
              }}
              title="Edit GitHub link"
              aria-label={`Edit GitHub link for ${m.username || m.email || 'member'}`}
            >
              <Link2 className="h-4 w-4" aria-hidden />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: 'streamCount',
      header: 'Streams',
      cell: ({ row }) => {
        const n = row.original.streamCount;
        return (
          <span className="inline-flex items-center gap-1 text-sm text-slate-400">
            <Waves className="w-4 h-4 text-vivid-cyan-blue flex-shrink-0" aria-hidden />
            {typeof n === 'number' ? n : '—'}
          </span>
        );
      },
    },
    {
      accessorKey: 'projectCount',
      header: 'Projects',
      cell: ({ row }) => {
        const n = row.original.projectCount;
        return (
          <span className="inline-flex items-center gap-1 text-sm text-slate-400">
            <FolderKanban className="w-4 h-4 text-vivid-purple flex-shrink-0" aria-hidden />
            {typeof n === 'number' ? n : '—'}
          </span>
        );
      },
    },
    {
      accessorKey: 'repositoryCount',
      header: 'Repos',
      cell: ({ row }) => {
        const n = row.original.repositoryCount;
        return (
          <span className="inline-flex items-center gap-1 text-sm text-slate-400">
            <GitFork className="w-4 h-4 text-vivid-red flex-shrink-0" aria-hidden />
            {typeof n === 'number' ? n : '—'}
          </span>
        );
      },
    },
  ];
}
